import { Button, MultiSelect, MultiSelectOptionType } from '@sixfold/common-ui';
import { useState } from 'react';

import { Tags } from '../tags';

interface Tag {
  name: string;
}

interface Props {
  tags: Tag[];
  availableTags: Tag[];
  addTag: (tagName: string) => void;
  removeTag: (tagName: string) => void;
}

export function TagsInput({ tags, availableTags, addTag, removeTag }: Props) {
  const [isEditing, setIsEditing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pendingTags, setPendingTags] = useState<string[]>([]);
  const [pendingRemovals, setPendingRemovals] = useState<string[]>([]);

  const tagNames = tags.map((t) => t.name);
  const activePendingTags = pendingTags.filter((p) => !tagNames.includes(p) && !pendingRemovals.includes(p));
  const activePendingRemovals = pendingRemovals.filter((r) => tagNames.includes(r));

  const allTags = [
    ...tags.filter((t) => !activePendingRemovals.includes(t.name)),
    ...activePendingTags.map((name) => ({ name })),
  ];

  const handleSetIsEditing = (editing: boolean) => {
    if (!editing) {
      setError(null);
    }
    setIsEditing(editing);
  };

  const handleChange = (newOptions: ReadonlyArray<MultiSelectOptionType>) => {
    if (!Array.isArray(newOptions) || newOptions.length === 0) {
      tags.forEach((tag) => removeTag(tag.name));
      setPendingRemovals((prev) => [...prev, ...tags.map((t) => t.name)]);
      setPendingTags([]);
      setError(null);
      return;
    }

    const newTagNames = newOptions.map((opt) => opt.value);

    setError(null);

    const removedTags = tags.filter((existingTag) => {
      return newTagNames.indexOf(existingTag.name) === -1;
    });
    removedTags.forEach((removedTag) => {
      removeTag(removedTag.name);
    });
    setPendingRemovals((prev) => [...prev, ...removedTags.map((t) => t.name)]);
    setPendingTags((prev) => prev.filter((p) => newTagNames.includes(p)));

    const addedTagNames = newTagNames.filter((newTagName) => {
      return tags.find((existingTag) => existingTag.name === newTagName) === undefined;
    });
    addedTagNames.forEach((addedTagName) => {
      const formattedTagName = addedTagName.trim();

      if (formattedTagName.toLowerCase() !== formattedTagName) {
        setError('Tag names may only be lowercase');
        return;
      }

      if (formattedTagName.length === 0) {
        return;
      }

      setPendingTags((prev) => [...prev, formattedTagName]);
      setPendingRemovals((prev) => prev.filter((r) => r !== formattedTagName));
      addTag(formattedTagName);
    });
  };

  const options = availableTags.map((tag): MultiSelectOptionType => ({ value: tag.name, label: tag.name }));
  const value = allTags.map((tag): MultiSelectOptionType => ({ value: tag.name, label: tag.name }));

  if (!isEditing) {
    if (allTags.length > 0) {
      return (
        <span
          style={{ display: 'inline-flex', alignItems: 'center', cursor: 'pointer', marginRight: 3 }}
          title="Click to add or change tags"
          onClick={() => handleSetIsEditing(true)}>
          <Tags tags={allTags} />
        </span>
      );
    }
    return (
      <Button kind="neutral" size="small" onClick={() => handleSetIsEditing(true)}>
        Add tags
      </Button>
    );
  }

  return (
    <div style={{ width: 260 }}>
      {error !== null && <div style={{ color: 'red', marginBottom: 10 }}>{error}</div>}
      <MultiSelect
        placeholder="No tags. Click to add some..."
        creatable
        options={options}
        value={value}
        size="small"
        onMenuClose={() => handleSetIsEditing(false)}
        onChange={handleChange}
        autoFocus
        {...{ closeMenuOnSelect: false }}
      />
    </div>
  );
}
